import { useQueryClient } from "@tanstack/react-query";
import { DatabaseZap, RefreshCw, ServerOff } from "lucide-react";
import { useTranslation } from "react-i18next";

import { VaultMark } from "@/components/brand/VaultMark";
import { Button } from "@/components/ui/Button";
import { type ServiceProblem } from "@/lib/service-problems";
import { useActiveServiceProblem } from "@/lib/use-service-problem";

function ProblemText({ problem }: { problem: ServiceProblem }) {
  const { t } = useTranslation();
  return (
    <>
      <p className="text-sm text-ink-muted">{t(`service.${problem}.body`)}</p>
      <p className="text-sm text-ink-muted">
        <span className="font-medium text-ink">{t("service.adminHint")}:</span> {t(`service.${problem}.hint`)}
      </p>
    </>
  );
}

/** Full-page explanation used when the app cannot even tell who the user is. */
export function ServiceUnavailablePage({
  problem,
  onRetry,
}: {
  problem: ServiceProblem;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const Icon = problem === "server_unreachable" ? ServerOff : DatabaseZap;
  return (
    <main id="main" className="grid min-h-dvh place-items-center bg-canvas px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="mb-8 flex items-center gap-3">
          <VaultMark />
          <span className="text-lg font-semibold tracking-tight">{t("app.name")}</span>
        </div>
        <section className="space-y-4 rounded-card border border-line bg-surface p-6 shadow-card sm:p-8">
          <span className="grid size-12 place-items-center rounded-full bg-danger-soft text-danger">
            <Icon className="size-6" aria-hidden="true" />
          </span>
          <h1 className="text-xl leading-snug font-semibold">{t(`service.${problem}.title`)}</h1>
          <ProblemText problem={problem} />
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button onClick={onRetry}>
              <RefreshCw className="size-4" aria-hidden="true" />
              {t("service.retry")}
            </Button>
            <span className="text-xs text-ink-subtle">{t("service.autoRetry")}</span>
          </div>
        </section>
      </div>
    </main>
  );
}

/** Banner shown inside the app when the database or the server fails during a session. */
export function ServiceProblemBanner() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const problem = useActiveServiceProblem();
  if (!problem) return null;
  return (
    <div
      role="alert"
      aria-label={t("service.banner")}
      className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger sm:px-6"
    >
      <DatabaseZap className="size-5 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{t(`service.${problem}.title`)}</p>
        <p className="text-danger/90">
          {t("service.adminHint")}: {t(`service.${problem}.hint`)}
        </p>
      </div>
      <Button variant="danger" onClick={() => void queryClient.refetchQueries({ type: "active" })}>
        <RefreshCw className="size-4" aria-hidden="true" />
        {t("service.retry")}
      </Button>
    </div>
  );
}
